import { Route } from 'vue-router'
import { GetLeakById, ReportLeak, CancelReport, AssignRepairDept, CancelAssignRepairDept, AssignRepairUser, CancelAssignRepairUser, CancelEndRepair, DeleteLeak, MarkAsInternalRepair } from '@/api/leaks'
import { ILeakPoint, ILeakDetection, ILeakRepair, LeakStatus, FileType, IDept, RepairType, IProject, IAllUserInfo, GetRepairType, GetLeakStatus, DeptType, IAllDeptInfo, IAllProjectInfo } from '@/api/types'
import { ListDepts, ListOrgUsers, ListProjects } from '@/api/orgAdmin'
import { formatDate } from '@/utils/index'
import { IsMobile, HasPer } from '@/utils/check'
import { UserModule } from '@/store/modules/user'
import { goBack } from '@/router'

/**
 * 漏点详情服务类
 * 处理漏点详情相关的业务逻辑
 */
export class LeakDetailService {
  public loading = false
  public saving = false
  public leak: ILeakPoint | null = null
  public leakId = ''
  public activeTab = 'basic'
  public showNavigation = false
  public assignDeptDialogVisible = false
  public repairDeptOptions: IDept[] = []
  public projectOptions: IProject[] = []
  public detectUsers: IAllUserInfo[] = []
  public repairUsers: IAllUserInfo[] = []

  // 分配部门相关
  public assignDeptForm = {
    deptId: undefined as number | undefined
  }

  public assignDeptRules = {
    deptId: [{ required: true, message: '请选择维修队伍', trigger: 'change' }]
  }

  // 图片查看器配置
  public viewerOptions = {
    navbar: true,
    title: false,
    toolbar: {
      zoomIn: true,
      zoomOut: true,
      oneToOne: true,
      reset: true,
      prev: true,
      next: true,
      rotateLeft: true,
      rotateRight: true,
      flipHorizontal: true,
      flipVertical: true,
    }
  }

  // 视频播放器配置
  private playerOptions = {
    playbackRates: [0.5, 1, 1.5, 2],
    autoplay: false,
    muted: false,
    loop: false,
    preload: 'auto',
    language: 'zh-CN',
    aspectRatio: '16:9',
    fluid: true,
    poster: '',
    width: '100%',
    height: '100%',
    techOrder: ['flash', 'html5'],
    controlBar: {
      fullscreenToggle: true
    },
    html5: {
      nativeControlsForTouch: true,
    },
    preferFullScreen: true,
  }

  constructor(
    private route: Route,
    private router: any,
    private refs: any,
    private message: any,
    private confirm: any
  ) {}

  /**
   * 组件创建时的初始化
   */
  public async created() {
    // 从路由参数获取漏点ID
    this.leakId = this.route.params.id
    if (this.leakId) {
      await this.fetchLeakDetail()
      console.log('漏点ID', this.leak)
    } else {
      this.message.error('漏点ID不存在')
      goBack()
    }
  }

  get isMobile() {
    return IsMobile()
  }

  // 权限相关计算属性
  get canEidtDetection(): boolean {
    return HasPer(["上帝", "漏点管理", "漏点检测"])
  }

  get canEditRepair(): boolean {
    return HasPer(["上帝", "漏点管理", "漏点维修"])
  }

  get canAssign(): boolean {
    return HasPer(['上帝','漏点管理'])
  }

  get canClaim(): boolean {
    return HasPer(['上帝','漏点管理','漏点维修'])
  }

  get canReport(): boolean {
    return HasPer(['上帝','漏点管理','漏点检测'])
  }

  get canCancelReport(): boolean {
    return HasPer(['上帝','漏点管理','漏点检测'])
  }

  get canAssignRepairDept(): boolean {
    return HasPer(['上帝','漏点管理'])
  }

  get canCancelAssignRepairDept(): boolean {
    return HasPer(['上帝','漏点管理'])
  }

  get canAssignRepairUser(): boolean {
    return HasPer(['上帝','漏点管理','漏点维修'])
  }

  get canCancelAssignRepairUser(): boolean {
    return HasPer(['上帝','漏点管理','漏点维修'])
  }

  get canFinishRepair(): boolean {
    return HasPer(['上帝','漏点管理','漏点维修'])
  }

  // 操作按钮显示控制
  get showEditDetectionBtn(): boolean {
    if (!this.leak) return false

    if (this.leak.leakStatus === LeakStatus.Unreported) {
      return this.canEidtDetection
    }
    return false
  }

  get showDeleteBtn(): boolean {
    if (!this.leak) return false
    if (this.leak.leakStatus === LeakStatus.Unreported) {
      return this.canEidtDetection
    }
    return false
  }

  get showReportBtn(): boolean {
    if (!this.leak) return false
    if (this.leak.leakStatus === LeakStatus.Unreported) {
      return this.canReport
    }
    return false
  }

  get showCancelReportBtn(): boolean {
    if (!this.leak) return false
    if (this.leak.leakStatus === LeakStatus.NeedAssignRepairDept) {
      return this.canCancelReport
    }
    return false
  }

  get showAssignBtn(): boolean {
    if (!this.leak) return false
    if (this.leak.leakStatus === LeakStatus.NeedAssignRepairDept) {
      return this.canAssignRepairDept
    }
    return false
  }

  get showInternalRepairBtn(): boolean {
    if (!this.leak) return false
    if (this.leak.leakStatus === LeakStatus.NeedAssignRepairDept) {
      return this.canAssignRepairDept
    }
    return false
  }

  get showCancelAssignBtn(): boolean {
    if (!this.leak) return false
    if (this.leak.leakStatus === LeakStatus.NeedAssignRepairUser) {
      return this.canCancelAssignRepairUser
    }
    return false
  }

  get showClaimBtn(): boolean {
    if (!this.leak) return false
    if (this.leak.leakStatus === LeakStatus.NeedAssignRepairUser) {
      return this.canAssignRepairUser
    }
    return false
  }

  get showCancelClaimBtn(): boolean {
    if (!this.leak) return false
    if (this.leak.leakStatus === LeakStatus.Repairing) {
      return this.canCancelAssignRepairUser
    }
    return false
  }

  get showFinishBtn(): boolean {
    if (!this.leak) return false
    if (this.leak.leakStatus === LeakStatus.Repairing) {
      return this.canFinishRepair
    }
    return false
  }

  get showCancelRepairBtn(): boolean {
    if (!this.leak) return false
    if (this.leak.leakStatus === LeakStatus.Repaired ||
        this.leak.leakStatus === LeakStatus.NoNeedRepair ||
        this.leak.leakStatus === LeakStatus.CantRepair) {
      return this.canFinishRepair
    }
    return false
  }

  get showEditRepairBtn(): boolean {
    if (!this.leak) return false

    if (this.leak.leakStatus === LeakStatus.Repairing) {
      return this.canEditRepair
    }
    return false
  }

  get basicTableData() {
    if (!this.leak) return []

    const data = [
      { label: '漏点编号', value: this.leak.index || '-' },
      { label: '漏点状态', value: this.getStatusText(this.leak.leakStatus) },
      { label: '创建时间', value: formatDate(this.leak.createTime) || '-' },
      { label: '参考位置', value: this.getLeakAddress() || '-' },
      { label: '所属项目', value: this.getProjectName(this.leak.projectId) }
    ]

    return data
  }

  get detectionTableData() {
    if (!this.leak || !this.leak.detection) return []

    return [
      { label: '路面信息', value: this.leak.detection.roadInfo || '-' },
      { label: '管径', value: this.leak.detection.size ? `${this.leak.detection.size}mm` : '-' },
      { label: '管材', value: this.leak.detection.material || '-' },
      { label: '参考地址', value: this.leak.detection.referenceAddr || '-' },
      { label: '详细地址', value: this.leak.detection.detailAddr || '-' },
      { label: '检测时间', value: formatDate(this.leak.detection.updateTime) || '-' },
      { label: '检测人员', value: this.getDetectUserName(this.leak.detection.checkUserId) }
    ]
  }

  get leakPosition(): [number, number] | undefined {
    if (this.leak && this.leak.detection && this.leak.detection.location) {
      return [this.leak.detection.location.longitude, this.leak.detection.location.latitude]
    }
    return undefined
  }

  get detectionImages() {
    if (!this.leak || !this.leak.detection || !this.leak.detection.files) return []
    return this.leak.detection.files
      .filter(file => file.fileType === FileType.CheckImage)
      .map(file => file.filePath)
  }

  get detectionVideos() {
    if (!this.leak || !this.leak.detection || !this.leak.detection.files) return []
    return this.leak.detection.files
      .filter(file => file.fileType === FileType.CheckVideo)
      .map(file => file.filePath)
  }

  get referenceImages() {
    if (!this.leak || !this.leak.detection || !this.leak.detection.files) return []
    return this.leak.detection.files
      .filter(file => file.fileType === FileType.ReferenceImage)
      .map(file => file.filePath)
  }

  get hasDetectionImages() {
    return this.detectionImages.length > 0
  }

  get hasDetectionVideos() {
    return this.detectionVideos.length > 0
  }

  get hasReferenceImages() {
    return this.referenceImages.length > 0
  }

  // 添加维修表格数据计算属性
  get repairTableData() {
    if (!this.leak || !this.leak.repair) return []

    return [
      { label: '维修类型', value: this.getRepairTypeText(this.leak.repair.repairType) },
      { label: '维修部位', value: this.leak.repair.part || '-' },
      { label: '漏水原因', value: this.leak.repair.reason || '-' },
      { label: '漏量(L/h)', value: this.leak.repair.leakLeakage || '-' },
      { label: '埋深(m)', value: this.leak.repair.depth || '-' },
      { label: '路面信息', value: this.leak.repair.roadInfo || '-' },
      { label: '管径', value: this.leak.repair.size ? `${this.leak.repair.size}mm` : '-' },
      { label: '管材', value: this.leak.repair.material || '-' },
      { label: '维修时间', value: formatDate(this.leak.repair.repairTime) || '-' },
      { label: '维修人员', value: this.getRepairUserName(this.leak.repair.repairUserId) }
    ]
  }

  // 添加维修图片和视频的计算属性
  get repairImages() {
    if (!this.leak || !this.leak.repair || !this.leak.repair.files) return []
    return this.leak.repair.files
      .filter(file => file.fileType === FileType.RepairImage)
      .map(file => file.filePath)
  }

  get repairVideos() {
    if (!this.leak || !this.leak.repair || !this.leak.repair.files) return []
    return this.leak.repair.files
      .filter(file => file.fileType === FileType.RepairVideo)
      .map(file => file.filePath)
  }

  get hasRepairImages() {
    return this.repairImages.length > 0
  }

  get hasRepairVideos() {
    return this.repairVideos.length > 0
  }

  /**
   * 获取漏点详情
   */
  public async fetchLeakDetail() {
    try {
      this.loading = true
      const response = await GetLeakById({ id: Number(this.leakId) })
      this.leak = response.data

      // 获取项目和用户数据
      await this.fetchOptions()
    } catch (error) {
      console.error('获取漏点详情失败', error)
      this.message.error('获取漏点详情失败')
    } finally {
      this.loading = false
    }
  }

  /**
   * 获取选项数据 项目、用户、检测人员、维修人员、维修部门
   */
  public async fetchOptions() {
    // 直接从route params获取数据
    const routeParams = this.route.params
    try {
      // 如果路由中包含项目数据，直接使用
      if (this.leak && this.leak.projectId) {
        if (routeParams.projects) {
          // 若提供了项目名称，则构建项目对象
          this.projectOptions = JSON.parse(routeParams.projects as string)
        } else {
          // 没有传入时才通过API获取
          const projectResponse = await ListProjects()
          const projects = projectResponse.data || []
          this.projectOptions = projects.filter(
            (project: IAllProjectInfo) => project.project.id === this.leak?.projectId
          ).map((project: IAllProjectInfo) => project.project)
        }
      }

      let users = []

      // 检测人员信息
      if (this.leak && this.leak.detection && this.leak.detection.checkUserId) {
        // 若提供了检测人员名称，则构建用户对象
        if (routeParams.checkUsers) {
          this.detectUsers = JSON.parse(routeParams.checkUsers as string)
        } else {
          if (users.length === 0){
            const userResponse = await ListOrgUsers()
            users = userResponse.data.items || []
          }
          this.detectUsers = users.filter(
            (user: IAllUserInfo) => user.user.id === this.leak?.detection?.checkUserId
          )
        }
      }

      // 维修人员信息
      if (this.leak && this.leak.repair && this.leak.repair.repairUserId) {
        // 若提供了维修人员名称，则构建用户对象
        if (routeParams.repairUsers) {
          this.repairUsers = JSON.parse(routeParams.repairUsers as string)
        } else {
          if (users.length === 0){
            const userResponse = await ListOrgUsers()
            users = userResponse.data.items || []
          }
          this.repairUsers = users.filter(
            (user: IAllUserInfo) => user.user.id === this.leak?.repair?.repairUserId
          )
        }
      }

      // 维修部门信息
      if (routeParams.repairDepts) {
        // 若提供了维修部门名称，则构建部门对象
        this.repairDeptOptions = JSON.parse(routeParams.repairDepts as string)
      } else {
        const deptResponse = await ListDepts()
        const depts = deptResponse.data || []
        this.repairDeptOptions = depts.filter(
          (dept: IAllDeptInfo) => dept.dept.type === DeptType.Repair
        ).map((dept: IAllDeptInfo) => dept.dept)
      }
    } catch (error) {
      console.error('获取选项数据失败', error)
      this.message.error('获取选项数据失败')
    }
  }

  /**
   * 删除漏点
   */
  public handleDelete() {
    if (!this.leak) return
    this.confirm('确认删除该漏点？此操作不可逆！', '警告', {
      confirmButtonText: '确定',
      cancelButtonText: '取消',
      type: 'warning'
    }).then(async() => {
      this.saving = true
      try {
        await DeleteLeak({ id: this.leak?.id || -1 })
        this.message.success('删除成功')

        // 删除成功后返回并刷新页面
        goBack()
      } catch (error) {
        console.error('删除漏点失败', error)
        this.message.error('删除漏点失败')
      } finally {
        this.saving = false
      }
    }).catch(() => {
      // 用户取消删除
    })
  }

  /**
   * 撤销上报
   */
  public handleCancelReport() {
    if (!this.leak) return
    this.confirm('确认撤销上报该漏点？', '提示', {
      confirmButtonText: '确定',
      cancelButtonText: '取消',
      type: 'warning'
    }).then(async() => {
      this.saving = true
      try {
        await CancelReport({ id: this.leak?.id || -1 })
        this.message.success('撤销上报成功')

        this.fetchLeakDetail()
      } catch (error) {
        console.error('撤销上报失败', error)
        this.message.error('撤销上报失败')
      } finally {
        this.saving = false
      }
    }).catch(() => {
      // 用户取消操作
    })
  }

  /**
   * 撤销分配
   */
  public handleCancelAssign() {
    if (!this.leak) return
    this.confirm('确认撤销分配维修部门？', '提示', {
      confirmButtonText: '确定',
      cancelButtonText: '取消',
      type: 'warning'
    }).then(async() => {
      this.saving = true
      try {
        await CancelAssignRepairDept({ id: this.leak?.id || -1 })
        this.message.success('撤销分配成功')
        this.fetchLeakDetail()
      } catch (error) {
        console.error('撤销分配失败', error)
        this.message.error('撤销分配失败')
      } finally {
        this.saving = false
      }
    }).catch(() => {
      // 用户取消操作
    })
  }

  /**
   * 认领漏点
   */
  public handleClaim() {
    if (!this.leak) return
    this.confirm('确认认领该漏点？', '提示', {
      confirmButtonText: '确定',
      cancelButtonText: '取消',
      type: 'warning'
    }).then(async() => {
      this.saving = true
      try {
        await AssignRepairUser({
          id: this.leak?.id || -1,
          userId: UserModule.userInfo?.id || -1 // 使用当前登录用户ID
        })
        this.message.success('认领成功')
        this.fetchLeakDetail()
      } catch (error) {
        console.error('认领漏点失败', error)
        this.message.error('认领漏点失败')
      } finally {
        this.saving = false
      }
    }).catch(() => {
      // 用户取消认领
    })
  }

  /**
   * 撤销认领
   */
  public handleCancelClaim() {
    if (!this.leak) return
    this.confirm('确认撤销认领该漏点？', '提示', {
      confirmButtonText: '确定',
      cancelButtonText: '取消',
      type: 'warning'
    }).then(async() => {
      this.saving = true
      try {
        await CancelAssignRepairUser({ id: this.leak?.id || -1 })
        this.message.success('撤销认领成功')
        this.fetchLeakDetail()
      } catch (error) {
        console.error('撤销认领失败', error)
        this.message.error('撤销认领失败')
      } finally {
        this.saving = false
      }
    }).catch(() => {
      // 用户取消操作
    })
  }

  /**
   * 撤销维修
   */
  public handleCancelRepair() {
    if (!this.leak) return
    this.confirm('确认撤销维修结果？漏点将恢复到维修中状态。', '提示', {
      confirmButtonText: '确定',
      cancelButtonText: '取消',
      type: 'warning'
    }).then(async() => {
      this.saving = true
      try {
        await CancelEndRepair({ id: this.leak?.id || -1 })
        this.message.success('撤销维修成功')
        this.fetchLeakDetail()
      } catch (error) {
        console.error('撤销维修失败', error)
        this.message.error('撤销维修失败')
      } finally {
        this.saving = false
      }
    }).catch(() => {
      // 用户取消操作
    })
  }

  /**
   * 内部维修
   */
  public async handleMarkInternalRepair() {
    if (!this.leak) return

    this.confirm('确认将该漏点标记为内部维修？', '提示', {
      confirmButtonText: '确定',
      cancelButtonText: '取消',
      type: 'warning'
    }).then(async() => {
      this.saving = true
      try {
        await MarkAsInternalRepair({ id: this.leak?.id || -1 })
        this.message.success('标记内部维修成功')
        await this.fetchLeakDetail()
      } catch (error) {
        console.error('标记内部维修失败', error)
        this.message.error('标记内部维修失败')
      } finally {
        this.saving = false
      }
    }).catch(() => {
      // 用户取消操作
    })
  }

  /**
   * 获取状态文本
   */
  public getStatusText(status: number): string {
    return GetLeakStatus(status)
  }

  /**
   * 获取维修类型文本
   */
  public getRepairTypeText(type?: number): string {
    if (!type) return '-'
    return GetRepairType(type)
  }

  /**
   * 获取漏点地址
   */
  public getLeakAddress(): string {
    if (!this.leak || !this.leak.detection) return ''
    return this.leak.detection.referenceAddr || this.leak.detection.detailAddr || ''
  }

  /**
   * 视频播放器选项
   */
  public getPlayerOptions(url: string) {
    return {
      ...this.playerOptions,
      sources: [{
        type: 'video/mp4',
        src: url
      }]
    }
  }

  /**
   * 打开导航
   */
  public openNavigation() {
    if (!this.leak) {
      this.message.warning('无法获取漏点信息')
      return
    }

    // 获取漏点位置信息
    let latitude, longitude;

    // 从漏点信息中提取经纬度坐标
    if (this.leak.detection && this.leak.detection.location &&
        this.leak.detection.location.latitude !== undefined &&
        this.leak.detection.location.longitude !== undefined) {
      // 从location对象中获取坐标
      latitude = this.leak.detection.location.latitude;
      longitude = this.leak.detection.location.longitude;
    }

    console.log("导航信息 - 经纬度:", longitude, latitude);

    if (latitude !== undefined && longitude !== undefined) {
      // 提取地址信息（优先使用参考地址，其次使用详细地址）
      let address = '';
      if (this.leak.detection && this.leak.detection.referenceAddr) {
        address = this.leak.detection.referenceAddr;
      } else if (this.leak.detection && this.leak.detection.detailAddr) {
        address = this.leak.detection.detailAddr;
      }

      console.log("导航信息 - 地址:", address);

      // 显示地图导航组件
      this.showNavigation = true;
    } else {
      this.message.warning('无法获取漏点位置信息')
    }
  }

  /**
   * 处理分配
   */
  public handleAssign() {
    if (!this.leak) return
    // 显示分配对话框
    this.assignDeptDialogVisible = true
  }

  /**
   * 确认分配维修部门
   */
  public async confirmAssignDept() {
    try {
      await (this.refs.assignDeptForm as any).validate()
    } catch (error) {
      return false
    }

    try {
      this.saving = true
      if (!this.assignDeptForm.deptId) {
        this.message.error('请选择维修部门')
        return
      }

      await AssignRepairDept({
        id: this.leak?.id || -1,
        deptId: this.assignDeptForm.deptId
      })

      this.message.success('分配维修部门成功')
      this.assignDeptDialogVisible = false
      this.fetchLeakDetail()
    } catch (error) {
      console.error('分配维修部门失败', error)
      this.message.error('分配维修部门失败')
    } finally {
      this.saving = false
    }
  }

  /**
   * 获取项目名称
   */
  public getProjectName(projectId?: number): string {
    if (!projectId) return '-'
    const project = this.projectOptions.find(p => p.id === projectId)
    return project ? project.name : '-'
  }

  /**
   * 获取检测人员名称
   */
  public getDetectUserName(userId?: number): string {
    if (!userId) return '-'
    const user = this.detectUsers.find(u => u.user.id === userId)
    return user ? user.user.username : '-'
  }

  /**
   * 获取维修人员名称
   */
  public getRepairUserName(userId?: number): string {
    if (!userId) return '-'
    const user = this.repairUsers.find(u => u.user.id === userId)
    return user ? user.user.username : '-'
  }

  /**
   * 返回列表页
   */
  public handleCancel() {
    goBack()
  }

  /**
   * 跳转到编辑页面
   * @param mode 编辑模式，可选值：detection, report, repair, finish
   */
  public goToEditPage(mode: string) {
    if (!this.leak) return

    // 构建路由参数
    const query: Record<string, string> = {
      mode: mode
    }

    // 将当前的一些数据作为参数传递到编辑页面
    if (this.projectOptions && this.projectOptions.length > 0) {
      query.projects = JSON.stringify(this.projectOptions)
    }

    if (this.detectUsers && this.detectUsers.length > 0) {
      query.checkUsers = JSON.stringify(this.detectUsers)
    }

    if (this.repairUsers && this.repairUsers.length > 0) {
      query.repairUsers = JSON.stringify(this.repairUsers)
    }

    query.leak = JSON.stringify(this.leak)

    if (mode === 'detection' || mode === 'report') {
      this.router.push({
        name: 'LeakEditDetection',
        params: {
          id: this.leakId,
          ...query
        }
      })
    } else if (mode === 'repair' || mode === 'finish') {
      this.router.push({
        name: 'LeakEditRepair',
        params: {
          id: this.leakId,
          ...query
        }
      })
    }
  }
}